import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { clsx } from 'clsx';
import { gql } from '@common/hooks';
import { Form, Col, Row, Radio, List } from 'antd';
import { Colors } from '@common/tokens';
import { SubmittalPacketsUploadInput } from 'works/legacy/components/submittal-packets-upload-input';

const INPUT_TYPES = {
  CHOOSE_EXISTING: 'CHOOSE_EXISTING',
  UPLOAD_NEW: 'UPLOAD_NEW',
};

const DESCRIPTIONS = {
  CHOOSE_EXISTING_SUBMITTAL_PACKET: `
    Select an existing submittal packet recently uploaded for this candidate.
  `,
};

function getSubmittalPacketLastFile(_submittalPacket = { files: [] }) {
  const { files } = _submittalPacket;
  return files[files.length - 1];
}

function getFormattedDate(dateValue) {
  return moment(dateValue).format('h:mmA, M/D/YYYY');
}

function isEmpty(submittalPackets) {
  return !submittalPackets || submittalPackets?.length === 0;
}

export function SubmittalPacketsMainInput({ submittalPackets, disabled }) {
  const [inputType, setInputType] = useState(
    isEmpty(submittalPackets)
      ? INPUT_TYPES.UPLOAD_NEW
      : INPUT_TYPES.CHOOSE_EXISTING
  );
  const [selectedSubmittalPacket, setSelectedSubmittalPacket] = useState('');

  function isSubmittalPacketSelected(item) {
    return selectedSubmittalPacket === item.id;
  }

  const availableFileNames = submittalPackets.map((_submittalPacket) => {
    const [file] = _submittalPacket?.files || [];
    return file?.filename || '';
  });

  return (
    <>
      <div className="submittal-packet-main-input">
        <Radio.Group
          disabled={disabled}
          defaultValue={inputType}
          onChange={({ target: { value } }) => setInputType(value)}
        >
          <Radio
            value={INPUT_TYPES.CHOOSE_EXISTING}
            disabled={isEmpty(submittalPackets)}
          >
            Choose existing
          </Radio>
          <Radio
            className="submittal-packet-main-input__radio--upload-new"
            value={INPUT_TYPES.UPLOAD_NEW}
          >
            Upload new
          </Radio>
        </Radio.Group>
        <Row>
          <Col xs={{ span: 24 }}>
            {inputType === INPUT_TYPES.UPLOAD_NEW ? (
              <SubmittalPacketsUploadInput
                availableFileNames={availableFileNames}
              />
            ) : (
              <>
                <Form.Item
                  name="submittalPacket"
                  valuePropName="file"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a file',
                    },
                  ]}
                >
                  <Radio.Group
                    style={{ display: 'block' }}
                    disabled={disabled}
                    onChange={(event) =>
                      setSelectedSubmittalPacket(event.target.value)
                    }
                  >
                    <p className="submittal-packet-description">
                      {DESCRIPTIONS.CHOOSE_EXISTING_SUBMITTAL_PACKET}
                    </p>
                    <List
                      bordered
                      dataSource={submittalPackets}
                      renderItem={(_submittalPacket) => {
                        if (_submittalPacket.archived) {
                          return <></>;
                        }

                        const lastFile =
                          getSubmittalPacketLastFile(_submittalPacket);

                        const isThisASelectedSubmittalPacket =
                          isSubmittalPacketSelected(_submittalPacket);

                        return (
                          <List.Item
                            style={{
                              backgroundColor: `${
                                isThisASelectedSubmittalPacket
                                  ? Colors.elmLightest
                                  : ''
                              }`,
                            }}
                          >
                            <Radio
                              className="submittal-packet-item-option"
                              value={_submittalPacket?.id}
                              key={_submittalPacket?.id}
                            >
                              <div className="submittal-packet-item-label-wrapper">
                                <div>
                                  <span
                                    className={clsx('submittal-packet-item', {
                                      'submittal-packet--bold':
                                        isThisASelectedSubmittalPacket,
                                    })}
                                  >
                                    {lastFile?.filename}
                                  </span>
                                </div>
                                <div>
                                  <span
                                    className={clsx(
                                      'submittal-packet-details',
                                      {
                                        'submittal-packet--bold':
                                          isThisASelectedSubmittalPacket,
                                      }
                                    )}
                                  >
                                    uploaded{' '}
                                    {getFormattedDate(lastFile?.createdAt)}
                                  </span>
                                </div>
                              </div>
                            </Radio>
                          </List.Item>
                        );
                      }}
                    ></List>
                  </Radio.Group>
                </Form.Item>
              </>
            )}
          </Col>
        </Row>
      </div>

      <style jsx>{`
        .submittal-packet-item {
          font-weight: 400;
          line-height: 14px;
        }
        :global(.submittal-packet-item-option.ant-radio-wrapper) {
          width: 100%;
        }
        :global(.submittal-packet-item-option .ant-radio) {
          vertical-align: super;
        }
        .submittal-packet-item-label-wrapper {
          display: inline-block;
        }
        .submittal-packet-details {
          color: ${Colors.midnightLighter};
          font-size: 13px;
          font-weight: 400;
          line-height: 16px;
        }
        .submittal-packet--bold {
          font-weight: 800;
        }
        .submittal-packet-main-input :global(.ant-upload) {
          margin-top: 1rem;
        }
        .submittal-packet-description {
          margin-top: 16px;
          font-size: 16px;
          line-height: 24px;
          font-weight: 400;
          color: ${Colors.midnightLighter};
        }
        .file-preview-link img {
          margin-bottom: 1rem;
        }
        .submittal-packet-upload-text {
          font-weight: 800;
          font-size: 16px;
          color: ${Colors.elm};
        }
      `}</style>
    </>
  );
}

SubmittalPacketsMainInput.fragments = {
  worksSubmittalPacket: gql`
    fragment WorksSubmittalPacket on WorksProfile {
      id
      worksSubmittalPackets {
        id
        files {
          id
          createdAt
          downloadUrl
          filename
          previewUrl
        }
      }
    }
  `,
};

SubmittalPacketsMainInput.propTypes = {
  submittalPackets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      files: PropTypes.arrayOf(
        PropTypes.shape({
          filename: PropTypes.string,
          downloadUrl: PropTypes.string,
          createdAt: PropTypes.string,
        })
      ),
    })
  ),
  disabled: PropTypes.bool,
};

SubmittalPacketsMainInput.defaultProps = {
  submittalPackets: [],
  disabled: false,
};
